<template>
    <div class="flex flex-col gap-4">
        <h3 class="text-muval-gray-1 text-sm font-medium">Notes</h3>

        <SalesActionNoteComposer
            v-if="canModify"
            ref="rootComposer"
            :submit="submitRoot"
            placeholder="Add a note"
            submitLabel="Add note"
            dataTest="company-diary-note-form"
        />

        <ErrorDetails :errorData="errorObject" />

        <MuvalLoader
            v-if="isLoading"
            class="mx-auto my-6"
            size="l"
        />

        <p
            v-else-if="!notes.length"
            class="text-muval-gray-2 py-6 text-center text-sm"
            data-test="company-diary-notes-empty"
        >
            No notes on this entry.
        </p>

        <div
            v-else
            class="flex flex-col gap-y-4"
        >
            <!--
                The note row is cloned from the Sales thread's markup rather than
                reused: that component is a modal of its own, wired to the
                sales-action endpoints, and parameterising it would touch a busy
                surface for no benefit here. The note text *is* shared - NoteText
                renders its links and mention highlights for both.
            -->
            <div
                v-for="note in notes"
                :key="note.id"
                class="flex flex-col gap-y-3"
                data-test="company-diary-note-thread"
            >
                <div
                    class="flex w-full gap-x-2"
                    data-test="company-diary-note"
                >
                    <img
                        v-if="note.author?.avatar"
                        :src="note.author.avatar"
                        class="hidden size-10 shrink-0 rounded-full object-cover lg:block"
                    />
                    <div
                        v-else
                        class="bg-muval-gray-5 hidden size-10 shrink-0 rounded-full lg:block"
                    />
                    <div class="flex min-w-0 flex-1 flex-col gap-y-1">
                        <!--
                            The composer replaces the note's own body while it is
                            being edited, rather than opening below it: two copies of
                            the same text on screen at once leaves no way to tell
                            which one is about to be saved.
                        -->
                        <SalesActionNoteComposer
                            v-if="editingId === note.id"
                            :ref="(el) => setEditComposer(el)"
                            :submit="(values) => submitEdit(note, values)"
                            :initialComment="note.comment ?? ''"
                            :initialMentionedUserIds="note.mentioned_user_ids ?? []"
                            submitLabel="Save note"
                            :submitIcon="null"
                            :rows="3"
                            cancellable
                            dataTest="company-diary-note-edit-form"
                            @cancel="editingId = null"
                        />
                        <div
                            v-else
                            class="card bg-muval-gray-6 w-full px-3 py-2 shadow-none"
                        >
                            <span
                                class="font-medium"
                                v-text="note.author?.fullname ?? note.agent_name ?? 'System'"
                            />
                            <div class="mt-2 text-sm wrap-break-word whitespace-pre-wrap">
                                <NoteText
                                    :comment="note.comment"
                                    :mentioned-users="note.mentioned_users"
                                    markup
                                />
                            </div>
                        </div>
                        <div class="text-muval-gray-2 ml-2 flex items-center gap-x-1.5 text-xs">
                            <span
                                class="whitespace-nowrap"
                                v-text="note.created_at_date_formatted"
                            />
                            <span
                                class="whitespace-nowrap"
                                v-text="note.created_at_time_formatted"
                            />
                            <span
                                v-if="wasEdited(note)"
                                class="whitespace-nowrap italic"
                                data-test="company-diary-note-edited"
                                >edited</span
                            >
                            <button
                                v-if="canModify"
                                type="button"
                                class="text-brand font-medium whitespace-nowrap hover:underline"
                                data-test="company-diary-note-reply-toggle"
                                @click="toggleReply(note.id)"
                            >
                                {{ replyingTo === note.id ? 'Cancel reply' : 'Reply' }}
                            </button>
                            <!--
                                Offered only to whoever the API would actually let
                                through - the author, or SUPER/ADMIN/OWNER - so a
                                refusal is never something the operator discovers
                                after retyping the note.
                            -->
                            <button
                                v-if="canModify && canEdit(note)"
                                type="button"
                                class="text-brand font-medium whitespace-nowrap hover:underline"
                                data-test="company-diary-note-edit-toggle"
                                @click="toggleEdit(note.id)"
                            >
                                {{ editingId === note.id ? 'Cancel edit' : 'Edit' }}
                            </button>
                        </div>
                    </div>
                </div>

                <!--
                    Replies are one level deep, so this nests exactly once and never
                    recurses. The rail marks them as belonging to the note above
                    rather than standing on their own in the thread.
                -->
                <div
                    v-if="note.replies?.length || replyingTo === note.id"
                    class="border-muval-gray-5 ml-4 flex flex-col gap-y-3 border-l-2 pl-3 lg:ml-12"
                >
                    <div
                        v-for="reply in note.replies ?? []"
                        :key="reply.id"
                        class="flex w-full gap-x-2"
                        data-test="company-diary-note-reply"
                    >
                        <img
                            v-if="reply.author?.avatar"
                            :src="reply.author.avatar"
                            class="hidden size-8 shrink-0 rounded-full object-cover lg:block"
                        />
                        <div
                            v-else
                            class="bg-muval-gray-5 hidden size-8 shrink-0 rounded-full lg:block"
                        />
                        <div class="flex min-w-0 flex-1 flex-col gap-y-1">
                            <SalesActionNoteComposer
                                v-if="editingId === reply.id"
                                :ref="(el) => setEditComposer(el)"
                                :submit="(values) => submitEdit(reply, values)"
                                :initialComment="reply.comment ?? ''"
                                :initialMentionedUserIds="reply.mentioned_user_ids ?? []"
                                submitLabel="Save reply"
                                :submitIcon="null"
                                :rows="2"
                                cancellable
                                dataTest="company-diary-reply-edit-form"
                                @cancel="editingId = null"
                            />
                            <div
                                v-else
                                class="card bg-muval-gray-6 w-full px-3 py-2 shadow-none"
                            >
                                <span
                                    class="font-medium"
                                    v-text="reply.author?.fullname ?? reply.agent_name ?? 'System'"
                                />
                                <div class="mt-2 text-sm wrap-break-word whitespace-pre-wrap">
                                    <NoteText
                                        :comment="reply.comment"
                                        :mentioned-users="reply.mentioned_users"
                                        markup
                                    />
                                </div>
                            </div>
                            <div class="text-muval-gray-2 ml-2 flex items-center gap-x-1.5 text-xs">
                                <span
                                    class="whitespace-nowrap"
                                    v-text="reply.created_at_date_formatted"
                                />
                                <span
                                    class="whitespace-nowrap"
                                    v-text="reply.created_at_time_formatted"
                                />
                                <span
                                    v-if="wasEdited(reply)"
                                    class="whitespace-nowrap italic"
                                    >edited</span
                                >
                                <button
                                    v-if="canModify && canEdit(reply)"
                                    type="button"
                                    class="text-brand font-medium whitespace-nowrap hover:underline"
                                    data-test="company-diary-reply-edit-toggle"
                                    @click="toggleEdit(reply.id)"
                                >
                                    {{ editingId === reply.id ? 'Cancel edit' : 'Edit' }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <SalesActionNoteComposer
                        v-if="replyingTo === note.id"
                        ref="replyComposer"
                        :submit="submitReply"
                        placeholder="Write a reply"
                        submitLabel="Reply"
                        :rows="2"
                        cancellable
                        dataTest="company-diary-note-reply-form"
                        @cancel="replyingTo = null"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { toast } from 'vue-sonner';
import ErrorDetails from '@components/errors/ErrorDetails.vue';
import MuvalLoader from '@components/loader/MuvalLoader.vue';
import SalesActionNoteComposer from '@components/sales/SalesActionNoteComposer.vue';
import { useCompanyDiaryNotes, type CompanyDiaryNote } from '@composables/useCompanyDiaryNotes';
import NoteText from '@components/NoteText.vue';

/**
 * The entry's note thread (spec company-diary-entries, "Notes and file
 * attachments").
 *
 * One level deep: the API returns roots newest-first, each carrying its replies
 * oldest-first, and a reply can only be written against a root. Nothing here
 * flattens or re-parents - a rejected reply surfaces its error rather than landing
 * somewhere else.
 */

const props = defineProps<{
    /** Null while the entry is unsaved - the thread hangs off its schedule service. */
    entryId: number | null;
    canModify: boolean;
}>();

const entryId = computed(() => props.entryId);

const { notes: fetched, isLoading, createNote, updateNote, canEdit } = useCompanyDiaryNotes(entryId);

const notes = computed<CompanyDiaryNote[]>(() => fetched.value ?? []);

/** The root whose reply composer is open. At most one at a time. */
const replyingTo = ref<number | null>(null);
const editingId = ref<number | null>(null);
const errorObject = ref<{ message: string | null; errors: unknown }>({ message: null, errors: null });

const rootComposer = ref<any>(null);
const replyComposer = ref<any>(null);
const editComposer = ref<any>(null);

/**
 * A `v-for` ancestor makes the reply composer's ref an array even though only one
 * is ever rendered, so it is unwrapped rather than assumed to be the instance.
 */
const activeReplyComposer = computed(() =>
    Array.isArray(replyComposer.value) ? (replyComposer.value[0] ?? null) : replyComposer.value,
);

const toggleReply = (noteId: number) => {
    replyingTo.value = replyingTo.value === noteId ? null : noteId;
    editingId.value = null;
    errorObject.value = { message: null, errors: null };
};

/**
 * One note is edited at a time, and opening an edit closes any reply in progress:
 * both would otherwise be open against the same note with two submit buttons and
 * no way to tell which is which.
 */
const toggleEdit = (noteId: number) => {
    editingId.value = editingId.value === noteId ? null : noteId;
    replyingTo.value = null;
    errorObject.value = { message: null, errors: null };
};

/**
 * Only ever one edit composer is rendered, but it sits under a `v-for`, so the ref
 * arrives per-element and the unmounting one reports `null` after the mounting one
 * has already registered. Ignoring the nulls keeps the live instance.
 */
const setEditComposer = (el: any) => {
    if (el) editComposer.value = el;
};

/**
 * Compared with a second of tolerance: `created_at` and `updated_at` are written
 * in the same transaction but not from the same clock read, so an untouched note
 * can differ by milliseconds and would otherwise be labelled as edited from the
 * moment it was created.
 */
const wasEdited = (note: CompanyDiaryNote): boolean => {
    if (!note.created_at || !note.updated_at) return false;

    return new Date(note.updated_at).getTime() - new Date(note.created_at).getTime() > 1000;
};

/**
 * Saves an edit, and closes the editor only if the write landed. A refusal leaves
 * the text in the box to be copied out rather than discarding what was typed.
 */
const submitEdit = async (note: CompanyDiaryNote, values: { comment: string; mentioned_user_ids: number[] }) => {
    errorObject.value = { message: null, errors: null };
    editComposer.value?.setErrors?.({});

    try {
        await updateNote.mutateAsync({ id: note.id, ...values });

        editingId.value = null;
        toast.success(note.parent_id ? 'Reply updated' : 'Note updated');
    } catch (error: any) {
        errorObject.value = { message: error?.message ?? null, errors: error?.errors ?? null };

        if (error?.errors) editComposer.value?.setErrors?.(error.errors);
    }
};

const submitRoot = (values: { comment: string; mentioned_user_ids: number[] }) =>
    write(values, null, () => rootComposer.value);

const submitReply = (values: { comment: string; mentioned_user_ids: number[] }) =>
    write(values, replyingTo.value, () => activeReplyComposer.value);

/**
 * The one write path. `parent_id` is the only difference between a root and a
 * reply, and the composer that submitted is the one that gets cleared and the one
 * that shows any field errors.
 */
async function write(
    values: { comment: string; mentioned_user_ids: number[] },
    parentId: number | null,
    composer: () => any,
) {
    errorObject.value = { message: null, errors: null };
    composer()?.setErrors?.({});

    try {
        await createNote.mutateAsync({ ...values, ...(parentId ? { parent_id: parentId } : {}) });

        composer()?.reset?.();

        if (parentId) replyingTo.value = null;

        toast.success(parentId ? 'Reply added' : 'Note added');
    } catch (error: any) {
        errorObject.value = { message: error?.message ?? null, errors: error?.errors ?? null };

        if (error?.errors) composer()?.setErrors?.(error.errors);
    }
}

/** An edit left open would reopen against whatever note sits at that id next time. */
watch(entryId, () => {
    replyingTo.value = null;
    editingId.value = null;
    errorObject.value = { message: null, errors: null };
});
</script>
